import { useState } from 'react';

const buildShareText = (passNumber) => {
  if (passNumber) {
    return `I just claimed Hall Pass № ${passNumber}! Get yours now before it's too late. Coming soon to Hayward, CA — the bay's best kept secret.`;
  }
  return "I got hall pass! Get yours now before it's too late. Coming soon to Hayward, CA — the bay's best kept secret.";
};

const IconX = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M18.244 2H21.5l-7.41 8.47L23 22h-6.79l-4.74-6.2L6.04 22H2.78l7.93-9.06L1.6 2h6.94l4.28 5.66L18.244 2Zm-2.38 18h1.88L7.27 4H5.27l10.59 16Z" />
  </svg>
);
const IconFacebook = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M13.5 21v-7.5h2.6l.4-3.1h-3V8.4c0-.9.3-1.5 1.6-1.5H17V4.1c-.3 0-1.3-.1-2.4-.1-2.4 0-4 1.5-4 4.1v2.2H8v3.1h2.6V21h2.9Z" />
  </svg>
);
const IconWhatsApp = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M20.5 3.5A10 10 0 0 0 3.6 16L2 22l6.1-1.6a10 10 0 0 0 4.8 1.2A10 10 0 0 0 20.5 3.5Zm-7.6 16.2a8.3 8.3 0 0 1-4.2-1.1l-.3-.2-3.6 1 1-3.5-.2-.3a8.3 8.3 0 1 1 7.3 4.1Zm4.6-6.2c-.3-.1-1.5-.7-1.7-.8-.2-.1-.4-.1-.6.1-.2.3-.7.8-.8.9-.2.2-.3.2-.6.1-.3-.1-1.1-.4-2-1.3-.8-.7-1.3-1.5-1.5-1.8-.1-.3 0-.4.1-.5l.4-.5c.1-.2.2-.3.3-.5 0-.2 0-.4 0-.5l-.7-1.7c-.2-.4-.4-.4-.6-.4h-.5c-.2 0-.5.1-.7.3-.3.3-1 1-1 2.4 0 1.4 1 2.7 1.2 2.9.1.2 2.1 3.3 5.2 4.6.7.3 1.3.5 1.8.6.7.2 1.4.2 2 .1.6-.1 1.5-.6 1.8-1.2.2-.6.2-1.1.1-1.3-.1-.1-.3-.2-.6-.3Z" />
  </svg>
);
const IconSms = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M21 11.5a8.38 8.38 0 0 1-8.5 8.5 8.5 8.5 0 0 1-3.78-.86L3 21l1.86-5.72A8.5 8.5 0 1 1 21 11.5Z" />
  </svg>
);
const IconInstagram = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
    <rect x="3" y="3" width="18" height="18" rx="5" />
    <circle cx="12" cy="12" r="4" />
    <circle cx="17.5" cy="6.5" r="1" fill="currentColor" />
  </svg>
);
const IconLink = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M10 13a5 5 0 0 0 7.07 0l3-3a5 5 0 0 0-7.07-7.07l-1.5 1.5" />
    <path d="M14 11a5 5 0 0 0-7.07 0l-3 3a5 5 0 0 0 7.07 7.07l1.5-1.5" />
  </svg>
);

export default function ShareButtons({ passNumber = null }) {
  const [copied, setCopied] = useState(false);
  const url =
    typeof window !== 'undefined'
      ? window.location.origin + window.location.pathname
      : '';
  const text = buildShareText(passNumber);
  const textWithUrl = `${text} ${url}`;

  const open = (href) => window.open(href, '_blank', 'noopener,noreferrer');

  const onX = () =>
    open(
      `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`
    );
  const onFb = () =>
    open(
      `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}&quote=${encodeURIComponent(text)}`
    );
  const onWa = () =>
    open(`https://api.whatsapp.com/send?text=${encodeURIComponent(textWithUrl)}`);
  const onSms = () => {
    window.location.href = `sms:?&body=${encodeURIComponent(textWithUrl)}`;
  };

  const copy = async (label = 'link') => {
    try {
      await navigator.clipboard.writeText(textWithUrl);
      setCopied(label);
      setTimeout(() => setCopied(false), 2200);
    } catch {
      setCopied('error');
      setTimeout(() => setCopied(false), 2200);
    }
  };

  const onIg = async () => {
    // Instagram has no web share intent — copy text+link so user can paste in story/DM.
    await copy('instagram');
  };

  const tryNative = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'The Hall Pass',
          text,
          url,
        });
        return true;
      } catch {
        /* user cancelled */
      }
    }
    return false;
  };

  const onCopy = async () => {
    const native = await tryNative();
    if (!native) await copy('link');
  };

  return (
    <div className="hp-share" data-testid="share-buttons">
      <p className="hp-share-label">Spread the word</p>
      <div className="hp-share-row">
        <button
          type="button"
          className="hp-share-btn"
          onClick={onX}
          aria-label="Share on X"
          data-testid="share-x"
        >
          <IconX /> <span>X</span>
        </button>
        <button
          type="button"
          className="hp-share-btn"
          onClick={onFb}
          aria-label="Share on Facebook"
          data-testid="share-facebook"
        >
          <IconFacebook /> <span>Facebook</span>
        </button>
        <button
          type="button"
          className="hp-share-btn"
          onClick={onIg}
          aria-label="Copy for Instagram"
          data-testid="share-instagram"
        >
          <IconInstagram /> <span>Instagram</span>
        </button>
        <button
          type="button"
          className="hp-share-btn"
          onClick={onWa}
          aria-label="Share on WhatsApp"
          data-testid="share-whatsapp"
        >
          <IconWhatsApp /> <span>WhatsApp</span>
        </button>
        <button
          type="button"
          className="hp-share-btn"
          onClick={onSms}
          aria-label="Share via SMS"
          data-testid="share-sms"
        >
          <IconSms /> <span>Text</span>
        </button>
        <button
          type="button"
          className="hp-share-btn"
          onClick={onCopy}
          aria-label="Copy link"
          data-testid="share-copy"
        >
          <IconLink /> <span>{copied === 'link' ? 'Copied' : 'Copy'}</span>
        </button>
      </div>
      {copied && (
        <p className="hp-share-toast" data-testid="share-toast">
          {copied === 'instagram'
            ? 'Copied — paste it into your Instagram story or DM.'
            : copied === 'error'
            ? "Couldn't copy. Long-press the link to copy manually."
            : 'Link copied.'}
        </p>
      )}
    </div>
  );
}
